import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ArrowLeft, MapPin, Printer, QrCode, Move, AlertTriangle, ImageIcon, Search, Boxes, ClipboardCheck, Save, X } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import PrintLabelDialog from '../components/PrintLabelDialog';

const FRONTEND_URL = window.location.origin;

const LocationViewPage = () => {
  const { id } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [printOpen, setPrintOpen] = useState(false);
  const [allLocations, setAllLocations] = useState([]);
  // Cycle count mode
  const [countMode, setCountMode] = useState(false);
  const [counts, setCounts] = useState({}); // { itemId: stringValue }
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [locItems, allLocs] = await Promise.all([
        api().get(`/locations/${id}/items`),
        api().get('/locations'),
      ]);
      setData(locItems.data);
      setAllLocations(allLocs.data || []);
    } catch (e) {
      // iter78v — auto-redirect to canonical env when this deployment doesn't
      // have the location (stale label from preview scanned on prod, etc.).
      if (e?.response?.status === 404) {
        const { tryAltEnvRedirect } = await import('../utils/altEnvRedirect');
        const redirected = await tryAltEnvRedirect(api);
        if (redirected) return;
      }
      toast.error('Failed to load location');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [id]);

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="spinner" /></div>;
  }
  if (!data) {
    return (
      <div className="text-center py-16 text-slate-500" data-testid="location-not-found">
        <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-amber-500" />
        Location not found.
        <div className="mt-4">
          <Button variant="outline" onClick={() => navigate('/locations')} className="rounded-sm">Back to Locations</Button>
        </div>
      </div>
    );
  }

  const loc = data.location;
  const qrTarget = `${FRONTEND_URL}/locations/view/${loc.id}`;
  const filtered = data.items.filter(i =>
    !search ||
    (i.item_sku || '').toLowerCase().includes(search.toLowerCase()) ||
    (i.item_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleDownloadQr = async () => {
    try {
      const res = await api().get(`/locations/${loc.id}/qr`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'image/png' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `loc_${loc.bin_id || loc.id}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download QR');
    }
  };

  const startCountMode = () => {
    const seeded = {};
    data.items.forEach((it) => { seeded[it.id] = String(it.current_quantity ?? 0); });
    setCounts(seeded);
    setCountMode(true);
  };

  const cancelCountMode = () => {
    setCountMode(false);
    setCounts({});
  };

  const handleSaveCounts = async () => {
    const payload = data.items
      .map((it) => {
        const raw = counts[it.id];
        if (raw === undefined || raw === '') return null;
        const counted = parseFloat(raw);
        if (Number.isNaN(counted)) return null;
        return { item_id: it.id, counted_quantity: counted };
      })
      .filter(Boolean);
    if (payload.length === 0) {
      toast.error('Enter at least one count');
      return;
    }
    setSubmitting(true);
    try {
      const { data: res } = await api().post(`/locations/${loc.id}/cycle-count`, { counts: payload });
      const adjusted = res.totals.adjustments;
      if (adjusted === 0) {
        toast(`No deltas — counts matched on-hand for all ${res.totals.counted} items.`);
      } else {
        toast.success(`Cycle count saved · ${adjusted} adjustment${adjusted === 1 ? '' : 's'} (net ${res.totals.net_delta >= 0 ? '+' : ''}${res.totals.net_delta})`);
      }
      setCountMode(false);
      setCounts({});
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cycle count failed');
    } finally {
      setSubmitting(false);
    }
  };

  const printPayload = {
    id: loc.id,
    item_sku: loc.bin_id,
    item_name: loc.location_name,
    vendor_sku: loc.placement || '',
    barcode_or_qr_value: qrTarget,
  };

  return (
    <div className="space-y-6 animate-slideIn" data-testid="location-view-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => navigate('/locations')} className="rounded-sm" data-testid="location-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-slate-900 truncate" data-testid="location-view-name">
              {loc.location_name}
            </h1>
            <p className="text-slate-500 text-sm mono">
              {loc.bin_id}
              {loc.placement && <span className="text-slate-400"> · {loc.placement}</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleDownloadQr} className="rounded-sm" data-testid="location-view-download-qr-btn">
            <QrCode className="w-4 h-4 mr-2" /> Download QR
          </Button>
          <Button variant="outline" onClick={() => setPrintOpen(true)} className="rounded-sm" data-testid="location-view-print-btn">
            <Printer className="w-4 h-4 mr-2" /> Print Label
          </Button>
          {!countMode && data.items.length > 0 && (
            <Button
              onClick={startCountMode}
              className="bg-orange-600 hover:bg-orange-700 rounded-sm"
              data-testid="location-view-cycle-count-btn"
            >
              <ClipboardCheck className="w-4 h-4 mr-2" /> Cycle Count
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* QR + meta */}
        <Card className="industrial-card lg:col-span-1">
          <CardContent className="p-5 space-y-4">
            <div className="flex flex-col items-center gap-2 bg-slate-50 border border-slate-200 rounded-sm p-4">
              <QRCodeSVG value={qrTarget} size={180} level="M" includeMargin={false} data-testid="location-view-qr" />
              <div className="text-xs text-slate-500 text-center break-all mono">{qrTarget}</div>
              <p className="text-xs text-slate-400 text-center">Scan to open this location's items page.</p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">Bin Type</div>
                <div className="text-slate-800">{loc.bin_type}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">Status</div>
                <span className={`status-badge ${loc.active_status ? 'status-released' : 'status-obsolete'}`}>
                  {loc.active_status ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="col-span-2">
                <div className="text-xs uppercase tracking-wider text-slate-500">Placement</div>
                <div className="text-slate-800" data-testid="location-view-placement">{loc.placement || <span className="text-slate-400">—</span>}</div>
              </div>
              {loc.parent_location_id && (() => {
                const p = allLocations.find(l => l.id === loc.parent_location_id);
                return (
                  <div className="col-span-2">
                    <div className="text-xs uppercase tracking-wider text-slate-500">Parent</div>
                    <button
                      type="button"
                      onClick={() => navigate(`/locations/view/${loc.parent_location_id}`)}
                      className="text-orange-600 hover:underline"
                    >
                      {p ? `${p.location_name} / ${p.bin_id}` : '—'}
                    </button>
                  </div>
                );
              })()}
            </div>
            {data.sub_locations.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Sub-locations ({data.sub_locations.length})</div>
                <ul className="text-sm space-y-1">
                  {data.sub_locations.map(sl => (
                    <li key={sl.id}>
                      <button
                        type="button"
                        onClick={() => navigate(`/locations/view/${sl.id}`)}
                        className="text-slate-700 hover:text-orange-600 hover:underline"
                        data-testid={`sub-location-link-${sl.id}`}
                      >
                        {sl.location_name} / {sl.bin_id}
                        {sl.placement && <span className="text-xs text-slate-400 ml-1">· {sl.placement}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Items list */}
        <Card className="industrial-card lg:col-span-2">
          <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between flex">
            <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Boxes className="w-4 h-4" /> {countMode ? 'Cycle Count Mode' : 'Items at This Location'}
            </h2>
            {countMode ? (
              <div className="flex gap-2" data-testid="cycle-count-action-row">
                <Button variant="ghost" size="sm" onClick={cancelCountMode} className="rounded-sm" data-testid="cycle-count-cancel-btn">
                  <X className="w-4 h-4 mr-1" /> Cancel
                </Button>
                <Button
                  onClick={handleSaveCounts}
                  disabled={submitting}
                  className="bg-emerald-600 hover:bg-emerald-700 rounded-sm"
                  data-testid="cycle-count-save-btn"
                >
                  <Save className="w-4 h-4 mr-1" /> {submitting ? 'Saving…' : 'Save Counts'}
                </Button>
              </div>
            ) : (
              <span className="text-xs text-slate-500 mono" data-testid="location-view-totals">
                {data.totals.items} item(s){data.totals.low_stock > 0 ? ` · ${data.totals.low_stock} low stock` : ''}
              </span>
            )}
          </CardHeader>
          <CardContent className="p-0">
            {!countMode && (
              <div className="p-3 border-b border-slate-100">
                <div className="relative max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Filter items..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 rounded-sm"
                    data-testid="location-view-item-search"
                  />
                </div>
              </div>
            )}
            {filtered.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500" data-testid="location-view-empty">
                {data.items.length === 0 ? 'No items stored here yet.' : 'No items match your filter.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Photo</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">{countMode ? 'On Hand' : 'Qty'}</TableHead>
                      {countMode && <TableHead className="text-right">Counted</TableHead>}
                      {countMode && <TableHead className="text-right">Δ</TableHead>}
                      {!countMode && <TableHead>Sub-location</TableHead>}
                      {!countMode && <TableHead>Placement</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(countMode ? data.items : filtered).map((it) => {
                      const counted = counts[it.id] === undefined ? '' : counts[it.id];
                      const parsedCount = parseFloat(counted);
                      const delta = !Number.isNaN(parsedCount) ? parsedCount - (it.current_quantity || 0) : null;
                      return (
                        <TableRow key={it.id} data-testid={`location-item-row-${it.id}`}>
                          <TableCell>
                            <button
                              onClick={() => navigate(`/items/view/${it.id}`)}
                              className="block w-10 h-10 bg-slate-50 border border-slate-200 rounded-sm overflow-hidden hover:border-orange-400 transition"
                              title="Open item"
                            >
                              {it.image_data ? (
                                <img src={it.image_data} alt={it.item_name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="flex items-center justify-center w-full h-full text-slate-300">
                                  <ImageIcon className="w-4 h-4" />
                                </div>
                              )}
                            </button>
                          </TableCell>
                          <TableCell className="mono">
                            <button
                              onClick={() => navigate(`/items/view/${it.id}`)}
                              className="text-slate-800 hover:text-orange-600 hover:underline"
                              data-testid={`location-item-sku-${it.id}`}
                            >
                              {it.item_sku}
                            </button>
                          </TableCell>
                          <TableCell>
                            <button
                              onClick={() => navigate(`/items/view/${it.id}`)}
                              className="text-slate-800 hover:text-orange-600 hover:underline text-left"
                            >
                              {it.item_name}
                            </button>
                          </TableCell>
                          <TableCell className="text-slate-500 text-sm">{it.item_category}</TableCell>
                          <TableCell className="text-right mono">
                            <span className={it.low_stock ? 'text-red-600 font-semibold' : ''}>
                              {it.current_quantity} {it.base_uom}
                            </span>
                          </TableCell>
                          {countMode && (
                            <TableCell className="text-right">
                              <Input
                                type="number"
                                step="0.01"
                                value={counted}
                                onChange={(e) => setCounts((prev) => ({ ...prev, [it.id]: e.target.value }))}
                                className="w-24 ml-auto rounded-sm mono text-right"
                                data-testid={`cycle-count-input-${it.id}`}
                              />
                            </TableCell>
                          )}
                          {countMode && (
                            <TableCell className="text-right mono" data-testid={`cycle-count-delta-${it.id}`}>
                              {delta === null ? <span className="text-slate-300">—</span> : (
                                <span className={delta === 0 ? 'text-slate-500' : delta > 0 ? 'text-emerald-700' : 'text-red-600'}>
                                  {delta > 0 ? `+${delta}` : delta}
                                </span>
                              )}
                            </TableCell>
                          )}
                          {!countMode && (
                            <TableCell className="text-sm text-slate-500">
                              {it.sub_location || <span className="text-slate-300">—</span>}
                            </TableCell>
                          )}
                          {!countMode && (
                            <TableCell className="text-sm text-slate-500">
                              {loc.placement || (it.match_kind === 'child' ? '↳ child' : '—')}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <PrintLabelDialog
        open={printOpen}
        onOpenChange={setPrintOpen}
        item={printPayload}
        labelType="Location"
      />
    </div>
  );
};

export default LocationViewPage;
